import React from 'react';
import { DateTime } from 'luxon';
import { Profile } from '../services/api';

interface Props {
  profile: Profile | null;
  loading?: boolean;
}

/**
 * The coach's card at the top of the public profile (43593:28743).
 *
 * It sits over the cover banner rather than beside the page in a sidebar: the
 * frame gives the whole width to the coach's own content, and the booking
 * buttons live on the session cards where the choice is actually made.
 *
 * Three lines, and only the ones we have. The frame shows "PGA Class A · 12 yrs
 * exp" — there is no years-of-experience anywhere in the payload, so the dot and
 * everything after it are left off rather than filled with something invented.
 */
const CoachHeaderCard: React.FC<Props> = ({ profile, loading }) => {
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ');
  const timeZoneLabel = formatTimeZone(profile?.timeZone);

  if (loading || !profile) {
    return (
      <div className="pub-coach-card" data-testid="public-coach-card">
        <div className="pub-skeleton pub-coach-card-avatar" />
        <div className="pub-coach-card-text">
          <div className="pub-skeleton pub-skeleton-title" />
          <div className="pub-skeleton pub-skeleton-description-line" />
        </div>
      </div>
    );
  }

  return (
    <div className="pub-coach-card" data-testid="public-coach-card">
      {profile.avatar ? (
        <img className="pub-coach-card-avatar" src={profile.avatar} alt={fullName} />
      ) : (
        <div className="pub-coach-card-avatar pub-coach-card-avatar-empty" aria-hidden="true" />
      )}

      <div className="pub-coach-card-text">
        <h1 className="pub-coach-card-name">{fullName}</h1>

        {profile.role && <p className="pub-coach-card-role">{profile.role}</p>}

        {timeZoneLabel && (
          <p className="pub-coach-card-timezone">
            <svg
              className="pub-coach-card-globe"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.3" />
              <path
                d="M1.75 8h12.5M8 1.75c1.6 1.7 2.5 3.9 2.5 6.25S9.6 12.55 8 14.25C6.4 12.55 5.5 10.35 5.5 8S6.4 3.45 8 1.75Z"
                stroke="currentColor"
                strokeWidth="1.3"
              />
            </svg>
            {timeZoneLabel}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * "(GMT-05:00) Eastern Time" — the offset read from the zone itself, so it
 * follows daylight saving rather than stating whatever was true when the coach
 * set it. An unknown zone is left off entirely.
 */
function formatTimeZone(zone?: string): string | null {
  if (!zone) {
    return null;
  }

  const now = DateTime.now().setZone(zone);

  if (!now.isValid) {
    return null;
  }

  const offset = now.toFormat('ZZ');
  const name = now.offsetNameLong || zone.split('/').pop()?.replace(/_/g, ' ');

  return `(GMT${offset}) ${name}`;
}

export default CoachHeaderCard;
